import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  reportsService,
  type WorkloadParams,
  type CptFrequencyParams,
  type CancerCasesParams,
  type BillingStatusParams,
  type PayablesParams,
  type TurnaroundTimeParams,
  type ManualTebraEntryParams,
  type SpecimenLogParams,
  type AccessionLogParams,
} from "../services/reports.service";
import { useAuthStore } from "../store/auth.store";

export function useWorkloadReport(
  params: WorkloadParams,
  page = 1,
  pageSize = 25
) {
  return useQuery({
    queryKey: ["reports", "workload", params, page, pageSize],
    queryFn: () => reportsService.getWorkload({ ...params, page, pageSize }),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.stage && params.startDate && params.endDate),
  });
}

export function useCptFrequencyReport(
  params: CptFrequencyParams,
  options: { pageSize?: number; enabled?: boolean } = {}
) {
  const userId = useAuthStore((state) => state.profile?.id);
  const { pageSize = 25, enabled = true } = options;
  return useQuery({
    queryKey: ["reports", "cpt-frequency", userId, params],
    queryFn: ({ signal }) =>
      reportsService.getCptFrequency({ ...params, page: 1, pageSize }, signal),
    enabled: Boolean(enabled && userId && params.startDate && params.endDate),
    staleTime: Infinity,
    gcTime: 8 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useSpecimenLogReport(params: SpecimenLogParams) {
  return useQuery({
    queryKey: ["reports", "specimen-log", params],
    queryFn: () => reportsService.getSpecimenLog(params),
    enabled: Boolean(params.startDate && params.endDate),
  });
}

export function useAccessionLogReport(params: AccessionLogParams) {
  const userId = useAuthStore((state) => state.profile?.id);
  return useQuery({
    queryKey: ["reports", "accession-log", userId, params],
    queryFn: ({ signal }) => reportsService.getAccessionLog(params, signal),
    enabled: Boolean(userId && params.startDate && params.endDate),
  });
}

export function useCancerCaseReport(
  params: CancerCasesParams,
  page = 1,
  pageSize = 25
) {
  return useQuery({
    queryKey: ["reports", "cancer-cases", params, page, pageSize],
    queryFn: () => reportsService.getCancerCases({ ...params, page, pageSize }),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.startDate && params.endDate),
  });
}

export function useBillingStatusReport(
  params: BillingStatusParams,
  page = 1,
  pageSize = 25
) {
  return useQuery({
    queryKey: ["reports", "billing-status", params, page, pageSize],
    queryFn: () =>
      reportsService.getBillingStatus({ ...params, page, pageSize }),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.startDate && params.endDate),
  });
}

export function useManualTebraEntryReport(
  params: ManualTebraEntryParams,
  page = 1,
  pageSize = 25
) {
  return useQuery({
    queryKey: ["reports", "manual-tebra-entry", params, page, pageSize],
    queryFn: () =>
      reportsService.getManualTebraEntry({ ...params, page, pageSize }),
    placeholderData: keepPreviousData,
    // The packet contains PHI/payer data. Keep it only while this report is
    // mounted; no local/session storage persistence is used.
    gcTime: 0,
  });
}

export function usePayablesReport(params: PayablesParams) {
  return useQuery({
    queryKey: ["reports", "payables", params],
    queryFn: () => reportsService.getPayables(params),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.startDate && params.endDate),
  });
}

export function useTurnaroundTimeReport(
  params: TurnaroundTimeParams,
  page = 1,
  pageSize = 25
) {
  return useQuery({
    queryKey: ["reports", "turnaround-time", params, page, pageSize],
    queryFn: () =>
      reportsService.getTurnaroundTime({ ...params, page, pageSize }),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.startDate && params.endDate),
  });
}
